import type {
  Difficulty,
  GameMode,
  PlayerColor,
  PlayerNetState,
  PlayerSummary,
  ServerMessage,
} from '../../shared/protocol';

const PLAYER_COLORS = new Set<PlayerColor>(['cyan', 'orange', 'violet', 'green']);

export const normalizeDifficulty = (value: unknown): Difficulty =>
  value === 'rubber' || value === 'nightmare' ? value : 'normal';

export const normalizeGameMode = (value: unknown): GameMode =>
  value === 'horde' ? 'horde' : 'campaign';

export const normalizePlayerColor = (value: unknown): PlayerColor =>
  PLAYER_COLORS.has(value as PlayerColor) ? value as PlayerColor : 'cyan';

const normalizePlayerSummary = (player: PlayerSummary, hostId: string): PlayerSummary => ({
  ...player,
  nickname: typeof player.nickname === 'string' && player.nickname.trim() ? player.nickname : 'Paperotto',
  color: normalizePlayerColor(player.color),
  isHost: typeof player.isHost === 'boolean' ? player.isHost : player.id === hostId,
  connected: typeof player.connected === 'boolean' ? player.connected : true,
});

const normalizePlayerState = (player: PlayerNetState): PlayerNetState => ({
  ...player,
  nickname: typeof player.nickname === 'string' && player.nickname.trim() ? player.nickname : 'Paperotto',
  color: normalizePlayerColor(player.color),
  health: Number.isFinite(player.health) ? player.health : 100,
  weapon: Number.isFinite(player.weapon) ? player.weapon : 0,
  paused: player.paused === true,
});

export const isLegacyServerMessage = (message: ServerMessage): boolean => {
  if (message.type === 'game-start') {
    return !['rubber', 'normal', 'nightmare'].includes(message.difficulty)
      || !['campaign', 'horde'].includes(message.mode);
  }
  if (message.type === 'player-state') return !PLAYER_COLORS.has(message.player.color);
  if (message.type === 'room-state') return message.players.some((player) => !PLAYER_COLORS.has(player.color));
  return false;
};

export const normalizeServerMessage = (message: ServerMessage): ServerMessage => {
  if (message.type === 'game-start') {
    return {
      ...message,
      difficulty: normalizeDifficulty(message.difficulty),
      mode: normalizeGameMode(message.mode),
    };
  }
  if (message.type === 'player-state') {
    return { ...message, player: normalizePlayerState(message.player) };
  }
  if (message.type === 'room-state') {
    return {
      ...message,
      players: Array.isArray(message.players)
        ? message.players.map((player) => normalizePlayerSummary(player, message.hostId))
        : [],
    };
  }
  return message;
};
